import React, {useCallback, useEffect, useMemo, useState} from 'react';
import useUserStore from 'common/stores/user';
import {useTranslation} from 'react-i18next';
import _ from 'lodash';

import UserInfo from 'common/components/UserInfo';
import {Button, Divider, Layout, Page} from '@phonectas/unifon-ui-kit';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import ProfileModel, {AnyUserSetting, DisplayPreferences, UserPreferences} from 'common/models/profileModel';
import ThreeSixtySettings from './ThreeSixtySettings';
import BasicSettings from './BasicSettings';
import {useUserRoles} from 'common/hooks/useUserRoles';
import PermissionManager from 'common/components/permissions/PermissionManager';
import {ROUTES} from 'common/constants';

const UserProfile: React.FC = () => {
	const user = useUserStore((state) => state.user);
	const [t] = useTranslation();
	const {canUseUserConfig} = useUserRoles();
	const hasThreeSixtyAccess = useMemo(
		() => (user ? new PermissionManager(user).hasAccess(ROUTES.THREE_SIXTY) : false),
		[user]
	);
	const {
		getUserConfig,
		setUserConfigBatch,
	} = useThreeSixtyNewStore((state) => ({
		getUserConfig: state.getUserConfig,
		setUserConfigBatch: state.setUserConfigBatch,
	}), shallow);
	const {userConfig} = useThreeSixtyNewStore(state => ({
		userConfig: state.userConfig,
	}), shallow);
	const [profileModel, setProfileModel] = useState<ProfileModel>(new ProfileModel());
	const [settings, setSettings] = useState<AnyUserSetting[]>([]);
	const [originalSettings, setOriginalSettings] = useState<AnyUserSetting[]>([]);
	const [hasChanges, setHasChanges] = useState(false);
	// The selects show the edited values, so Cancel only has to restore `settings`.
	const defaultRouteValue = settings.find((setting) => setting.key === 'default_route')?.value;
	const defaultRoute = typeof defaultRouteValue === 'string' ? defaultRouteValue : '';
	const feedFilterValue = settings.find((setting) => setting.key === 'account_feed_filter')?.value;
	const feedFilter = Array.isArray(feedFilterValue) ? feedFilterValue as string[] : [];

	useEffect(() => {
		const settingsChanged = !_.isEqual(settings, originalSettings);
		setHasChanges(settingsChanged);
	}, [settings, originalSettings]);

	useEffect(() => {
		const initialSettings = profileModel.toKeyValueSettings();
		setSettings(initialSettings);
		setOriginalSettings(initialSettings);
	}, [profileModel]);

	const handleResetSettings = useCallback(() => {
		setSettings(originalSettings);
	}, [originalSettings]);

	const handleSaveSettings = useCallback(() => {
		// Filter out the `user_key` setting
		const filteredSettings = settings.filter((setting) => setting.key !== 'user_key');
		const filteredOriginalSettings = originalSettings.filter((setting) => setting.key !== 'user_key');

		// Find only changed settings
		const changedSettings = filteredSettings.filter((setting) => {
			const originalSetting = filteredOriginalSettings.find(
				(orig) => orig.key === setting.key
			);
			return !_.isEqual(setting.value, originalSetting?.value);
		});

		// If no changes, return early
		if (changedSettings.length === 0) {
			return;
		}

		// Build a single object with all changes
		const configUpdates = changedSettings.reduce((acc, setting) => {
			acc[setting.key] = setting.value;
			return acc;
		}, {} as Record<string, string | number | boolean | string[] | unknown[] | DisplayPreferences | UserPreferences>);

		setUserConfigBatch(configUpdates);
	}, [settings, originalSettings, setUserConfigBatch]);

	const handleFilterChange = useCallback((value: string | string[]) => {
		setSettings((prevSettings) =>
			prevSettings.map((setting) =>
				setting.key === 'account_feed_filter'
					? {
						...setting,
						value: Array.isArray(value)
							? value.includes('')
								? []
								: value
							: value === ''
								? []
								: [value],
					}
					: setting
			)
		);
	}, []);

	const handleRouteChange = useCallback((value: string) => {
		setSettings((prevSettings) => {
			const existingSetting = prevSettings.find((setting) => setting.key === 'default_route');
			if (existingSetting) {
				return prevSettings.map((setting) =>
					setting.key === 'default_route'
						? {...setting, value}
						: setting
				);
			} else {
				return [...prevSettings, {key: 'default_route', value}];
			}
		});
	}, []);

	useEffect(() => {
		if (canUseUserConfig) {
			getUserConfig();
		}
	}, [getUserConfig, canUseUserConfig]);

	useEffect(() => {
		if (userConfig) {
			const updatedProfileModel = ProfileModel.fromApiResponse(userConfig);
			setProfileModel(updatedProfileModel);
		}
	}, [userConfig]);

	return (
		<>
			<Page.Section>
				<Layout direction="column" nowrap autoOverflow alignItems="center">
					<UserInfo data={user} />
				</Layout>
			</Page.Section>
			<Page.Section>
				<Layout fullWidth direction="column" gap="md" nowrap>
					{
						canUseUserConfig && (
							<>
								<BasicSettings
									value={defaultRoute}
									onChange={handleRouteChange}
								/>
								<Divider />
							</>
						)
					}
					{
						hasThreeSixtyAccess && (
							<ThreeSixtySettings
								feedFilter={feedFilter}
								onChange={handleFilterChange}
								showFeedFilter={canUseUserConfig}
							/>
						)
					}
					<Layout
						direction="row"
						nowrap
						autoOverflow
						justifyContent="end"
						alignItems="center"
						fullWidth>
						{
							hasChanges && (
								<Layout fullWidth>
									<Divider />
									<Layout gap="sm" nowrap justifyContent="end" fullWidth>
										<Button onClick={handleResetSettings} variant="secondary" color="error">
											{t('Cancel')}
										</Button>
										<Button onClick={handleSaveSettings} variant="primary" color="success">
											{t('Save')}
										</Button>
									</Layout>
								</Layout>
							)
						}
					</Layout>
				</Layout>
			</Page.Section>
		</>
	);
}

export default UserProfile;